// The SagaForge Arcade link (docs/ROADMAP.md D6, docs/ARCHITECTURE.md §14): the client's one way to the Arcade that
// embeds it, through the arcade game SDK (@scg/game-sdk). It tells the Arcade which phase the app is in — loading, the
// menu, a war — as the SDK's loading and gameplay events, so the Arcade's loading layer shows exactly while the client
// loads. The connection never delays the game: it is made alongside the page's start, and on arrival it catches the
// Arcade up with the phase the app has reached. Opened directly (not framed) the SDK answers standalone at once, and a
// site no Arcade embeds (local development) never connects: the game plays standalone as ever. Unlock checks join this
// link with the first item the Arcade sells (the catalogue is the user's call, ROADMAP §7 "Arcade unlocks").

import { ArcadeSdkError, initArcade, PRODUCTION_ARCADE_ORIGIN, STAGING_ARCADE_ORIGIN, type ArcadeClient, type GameEvent } from '@scg/game-sdk'

/** The game's id on the Arcade: its catalogue slug, which player tokens carry as their audience. */
const ARCADE_GAME_ID = 'taxwar'

/**
 * Which Arcade embeds each deployment (ARCHITECTURE §14): a build trusts only its own environment's Arcade, so the
 * staging Arcade can never drive the production game. One build serves both sites, so the page's origin decides.
 */
const ARCADE_OF_SITE: Readonly<Record<string, string>> = {
  'https://taxwar.sagaforge.ai': PRODUCTION_ARCADE_ORIGIN,
  'https://taxwar-staging.sagaforge.ai': STAGING_ARCADE_ORIGIN,
}

export type AppPhase = 'loading' | 'menu' | 'war'

/** The events that move the Arcade from one phase to another: its loading layer shows only while loading. */
const TRANSITIONS: Readonly<Record<AppPhase, Readonly<Record<AppPhase, readonly GameEvent[]>>>> = {
  loading: { loading: [], menu: ['loading_stop'], war: ['loading_stop', 'gameplay_start'] },
  menu: { loading: ['loading_start'], menu: [], war: ['gameplay_start'] },
  war: { loading: ['gameplay_stop', 'loading_start'], menu: ['gameplay_stop'], war: [] },
}

/** What an Arcade that connects now hears to catch up with the app's phase (it starts with no layer shown). */
const ARRIVAL: Readonly<Record<AppPhase, readonly GameEvent[]>> = {
  loading: ['loading_start'],
  menu: [],
  war: ['gameplay_start'],
}

export interface ArcadeLink {
  /** The app is now in `phase`; an embedding Arcade hears the change (or catches up once it connects). */
  enter(phase: AppPhase): void
}

/**
 * An SDK refusal (the Arcade gone, a timeout, another protocol version) is the Arcade's affair, never a fault of the
 * game's: logged, and the game plays on. Anything else is a bug and rejects unhandled, into the crash report (§13).
 */
function arcadeRefused(error: unknown): void {
  if (!(error instanceof ArcadeSdkError)) throw error
  console.warn(`SagaForge Arcade: ${error.code} (${error.message})`)
}

/** Connects the page at `site` (its origin) to the Arcade that embeds it; the app starts in the loading phase. */
export function linkArcade(site: string): ArcadeLink {
  let phase: AppPhase = 'loading'
  let client: ArcadeClient | null = null

  const report = (events: readonly GameEvent[]): void => {
    if (client === null) return
    for (const event of events) client.reportEvent(event).catch(arcadeRefused)
  }

  const arcade = ARCADE_OF_SITE[site]
  if (arcade !== undefined) {
    initArcade({ gameId: ARCADE_GAME_ID, arcadeOrigins: [arcade] }).then((connected) => {
      if (connected.mode !== 'embedded') return
      client = connected
      report(ARRIVAL[phase])
    }, arcadeRefused)
  }

  return {
    enter(next) {
      const events = TRANSITIONS[phase][next]
      phase = next
      report(events)
    },
  }
}
